import { Meal } from "@/clients/models/Meal";
import { Counter } from "@/components/ui/counter";
import { useGetScaledServings } from "@/hooks/requests/meal/use-get-scaled-servings";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo, useCallback, useMemo, useState } from "react";

interface MealIngredientsSectionProps {
  meal: Meal;
  mealDay?: string;
  targetDate?: string;
  fromPlan?: boolean;
}

export const MealIngredientsSection = memo(function MealIngredientsSection({
  meal,
  mealDay,
  targetDate,
  fromPlan,
}: MealIngredientsSectionProps) {
  const [servings, setServings] = useState(1);

  const { scaledIngredients, isLoading } = useGetScaledServings({
    mealId: meal.id ?? "",
    servingSize: servings,
    enabled: !!meal.id,
    source: fromPlan ? "plan" : "latest",
    targetDate,
  });

  const displayIngredients = useMemo(() => {
    return scaledIngredients.length > 0 ? scaledIngredients : meal.ingredients;
  }, [scaledIngredients, meal.ingredients]);

  const handleCountChange = useCallback((newCount: number) => {
    setServings(newCount);
  }, []);

  if (!meal.ingredients || meal.ingredients.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <h2 className={cn(textVariants.title3, "font-semibold text-center")}>Ingredients</h2>

      {meal.productComplexity?.toLowerCase() === "composite" && (
        <div className="flex w-full items-center justify-between rounded-[50px] bg-gray-50 px-1 pl-4 py-1" style={{ 'display' : 'none' }}>
          <h3 className={cn(textVariants.bodySingleLine, '!font-bold')}>Servings</h3>
          <div className="flex items-center gap-3">
            <span className={cn(textVariants.bodySingleLine, "!font-bold")}>{servings}</span>
            <Counter value={servings} onChange={handleCountChange} min={1} max={10} />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-0">
        {isLoading ? (
          <>
            {Array.from({ length: 5 }).map((_, i) => (
              <IonSkeletonText key={i} animated className="h-6 w-full rounded-l" />
            ))}
          </>
        ) : (
          displayIngredients.map((ingredient, index) => (
            <div key={index} className="flex items-start gap-3 py-3 px-2.5 border-b border-gray-50">
              <p className={cn(textVariants.bodySingleLine, "!font-medium")}>{ingredient}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
});
